import { ElementRef } from '@angular/core';
import { DateUtility } from '../../classes/dateUtility';
import { COLORS } from '../../interfaces/constants';
import { IHighChartCustomSeries } from '../available-water-chart/interfaces';
import highChartExportOptions from '../shared/highchartsExportOptions';
import { AppliedFertilizerChartDataSet, AppliedFertilizerChartRecord } from './interfaces';
import * as Highcharts from 'highcharts';

export class AppliedFertilizerChartService {
	private _chartData: Highcharts.IndividualSeriesOptions[];
	private min: number; // min max date number values for displaying the chart
	private max: number;

	constructor(
		private _chartContainer: ElementRef
	) {}

	public convertToGraphData(records: AppliedFertilizerChartDataSet): void {

		if (!records) {
			return;
		}

		let temp: {
			recommendations: IHighChartCustomSeries,
			AppliedAmounts: IHighChartCustomSeries,
			NUptake: Highcharts.LineChartSeriesOptions
		};

		temp = {
			recommendations: {
				name: 'Recommended',
				visible: true,
				data: [],
				tooltip: {
					valueSuffix: ' lbs N/acre'
				},
			},
			AppliedAmounts: {
				name: 'Fertilizer Applied',
				visible: true,
				data: [],
				tooltip: {
					valueSuffix: ' lbs N/acre'
				}
			},
			NUptake: {
				name: 'N Uptake',
				visible: true,
				data: [],
				tooltip: {
					valueSuffix: ' lbs N/acre'
				},
				marker: {
					enabled: false
				}
			}
		}

		for (let record of records.Recommendations) {
			let utc: number;

			utc = DateUtility.DateToUTC(DateUtility.DotNetToDate(record.EventDate));
			temp.recommendations.data.push([utc, record.Amount]);
		}

		for (let record of records.AppliedAmounts) {
			let utc: number;

			utc = DateUtility.DateToUTC(DateUtility.DotNetToDate(record.EventDate));
			temp.AppliedAmounts.data.push([utc, record.Amount]);
		}

		for (let record of records.NUptake) {
			let utc: number;

			utc = DateUtility.DateToUTC(DateUtility.DotNetToDate(record.EventDate));
			temp.NUptake.data.push([utc, record.Amount]);
		}

		const { minDate, maxDate } = records.NUptake.reduce((acc, curr) => {
			let date: number;

			date = DateUtility.DateToUTC(DateUtility.DotNetToDate(curr.EventDate));

			return {
				minDate: date < acc.minDate ? date : acc.minDate,
				maxDate: date > acc.maxDate ? date : acc.maxDate
			};
		}, { minDate: Infinity, maxDate: -Infinity });

		this.min = minDate;
		this.max = maxDate;

		this._chartData = [];

		this._chartData.push(temp.recommendations);
		this._chartData.push(temp.AppliedAmounts);
		this._chartData.push(temp.NUptake);

		this._chartData.push(this._generateDaysToNextFertilizaiton(records.DaysToNextFertilization,
			records.AppliedAmounts));
	}

	public drawChart(): Highcharts.ChartObject {
		let options: Highcharts.Options;

		if (!this._chartContainer) {
			return;
		}

		options = {
			chart: {
				height: 320,
				spacingBottom: 5,
			// type: 'line',
			},
			title: {
				text: null,
			},
			legend: {
				margin: 6,
				title: {
					text: null,
				}
			},
			tooltip: {
				backgroundColor: COLORS.WHITE,
				borderColor: COLORS.WHITE,
				valueDecimals: 2,
				valueSuffix: ' in.',
			},
			xAxis: {
				type: 'datetime',
				gridLineWidth: 1,
				min: this.min,
				max: this.max
			},
			yAxis: [{
				title: {
					text: 'Lbs N/acre'
				}
			}],
			series: this._chartData,
			exporting: highChartExportOptions
		}

		return Highcharts.chart(this._chartContainer.nativeElement, options);
	}

	private _generateDaysToNextFertilizaiton(daysRecords: AppliedFertilizerChartRecord[],
			appliedRecords: AppliedFertilizerChartRecord[]): Highcharts.LineChartSeriesOptions {

		let result: Highcharts.LineChartSeriesOptions;

		result = {
			name: 'Days to Next Fertilization',
			visible: true,
			data: [],
			marker: {
				enabled: false
			},
			tooltip: {
				pointFormatter: function () {
					return `
						<b>Days to Next Fertilization</b><br/>
						Days till next fertilization: ${this.custom} days
					`;
				}
			}
		}

		daysRecords.forEach((record, index) => {
			let utcStart: number;
			let utcEnd: number;

			utcStart = DateUtility.DateToUTC(DateUtility.DotNetToDate(record.EventDate));

			utcEnd = DateUtility.DateToUTC(
				DateUtility.addDays(DateUtility.DotNetToDate(record.EventDate), record.Amount)
			);

			result.data.push({
				x: utcStart,
				y: appliedRecords[index].Amount,
				custom: record.Amount} as any);

			result.data.push({
				x: utcEnd,
				y: appliedRecords[index].Amount,
				custom: record.Amount} as any);

			result.data.push(null);
		});

		return result;
	}
}
